import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { AiTable } from "./AiTable";
import { sanitizeHref } from "./chatMessageUtils";

// Links inside table cells match the engagement feed: sky blue, no underline
export const TABLE_CELL_CLASS =
  "border-b border-slate-200 px-2 py-1 align-top [&_a]:text-sky-600 [&_a]:no-underline [&_a:hover]:text-sky-800 [&_a:hover]:no-underline";

const components: Components = {
  h1: ({ children }) => <h3 className="mt-3 text-base font-semibold first:mt-0">{children}</h3>,
  h2: ({ children }) => <h3 className="mt-3 text-base font-semibold first:mt-0">{children}</h3>,
  h3: ({ children }) => <h4 className="mt-3 text-sm font-semibold first:mt-0">{children}</h4>,
  h4: ({ children }) => <h5 className="mt-2 text-sm font-semibold first:mt-0">{children}</h5>,
  h5: ({ children }) => <h5 className="mt-2 text-sm font-semibold first:mt-0">{children}</h5>,
  h6: ({ children }) => <h5 className="mt-2 text-sm font-semibold first:mt-0">{children}</h5>,
  p: ({ children }) => <p className="whitespace-pre-wrap">{children}</p>,
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-6">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-1 pl-6">{children}</ol>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-slate-300 pl-3 text-slate-600">{children}</blockquote>
  ),
  hr: () => <hr className="border-slate-200" />,
  a: ({ href, children }) => {
    const safeHref = sanitizeHref(href);
    if (!safeHref) {
      return <span>{children}</span>;
    }
    const external = /^https?:/i.test(safeHref);
    return (
      <a
        href={safeHref}
        className="text-kPurple-600 underline underline-offset-2 hover:text-kPurple-800"
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {children}
      </a>
    );
  },
  img: ({ alt }) => <span>{alt}</span>,
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-md bg-slate-100 p-3 text-xs">{children}</pre>
  ),
  code: ({ className, children }) => (
    <code className={`${className ?? ""} rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em]`}>
      {children}
    </code>
  ),
  table: ({ children }) => <AiTable>{children}</AiTable>,
  th: ({ children }) => (
    <th className="border-b border-slate-300 px-2 py-1 font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className={TABLE_CELL_CLASS}>{children}</td>,
};

export const AiMarkdown = ({ content }: { content: string }) => (
  <div className="space-y-3">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
      skipHtml
      urlTransform={(url) => sanitizeHref(url) ?? ""}
    >
      {content}
    </ReactMarkdown>
  </div>
);
